// -----------------------------------------------------------------------------
// Advanced, accurate scrolling for the single-page portfolio.
//
// Why a custom helper instead of scrollIntoView / scrollTo?
//   * `scrollIntoView({ behavior: 'smooth' })` scrolls every scrollable ancestor,
//     ignores the fixed navbar height, and only runs once. Because the sections
//     are lazy-loaded and the Projects grid loads images lazily too, the layout
//     often shifts AFTER an initial scroll — leaving the user in the wrong place
//     (e.g. the bottom of Projects instead of Contact).
//   * This helper computes the exact target Y (minus a navbar offset), scrolls
//     the window to it, then re-verifies the section position a few times and
//     corrects any drift caused by those late layout shifts.
// -----------------------------------------------------------------------------

// Height of the fixed navbar plus a little breathing room so section titles are
// never hidden underneath it.
const NAVBAR_OFFSET = 88;

// How long we keep waiting for the (lazy-loaded) target element to exist.
const ELEMENT_WAIT_MS = 5000;

/**
 * Returns the exact window scroll position that brings `el` into view,
 * accounting for the fixed navbar offset.
 */
export function getElementScrollY(el, offset = NAVBAR_OFFSET) {
  const rect = el.getBoundingClientRect();
  return Math.max(0, Math.round(window.scrollY + rect.top - offset));
}

/**
 * Animated scroll of the window to `top`. Uses the requested `behavior`
 * ('smooth' or 'auto'); falls back to an instant jump on browsers that do not
 * support { behavior: 'smooth' }.
 */
export function scrollToPosition(top, behavior = 'auto') {
  try {
    window.scrollTo({ top, behavior });
  } catch (_) {
    window.scrollTo(0, top);
  }
}

/** Instantly (or smoothly) scroll the window back to the very top. */
export function scrollToTop(behavior = 'auto') {
  scrollToPosition(0, behavior);
}

/**
 * Smoothly (or instantly) scrolls the window to the section with `sectionId`.
 *
 *  1. Polls until the element exists (sections render lazily).
 *  2. Waits for its position to stabilise — if images keep loading and the
 *     section keeps moving, keep waiting (up to `maxWait`).
 *  3. Smooth-scrolls to the exact position, then schedules a few follow-up
 *     checks that adjust only if the layout really shifted afterwards.
 *
 * `behavior` defaults to 'smooth' so navigation glides to the target instead of
 * jumping. Pass `{ behavior: 'auto' }` where an instant jump is preferred.
 */
export function scrollToSection(sectionId, options = {}) {
  const {
    offset = NAVBAR_OFFSET,
    maxWait = ELEMENT_WAIT_MS,
    behavior = 'smooth',
  } = options;

  const startedAt = Date.now();
  let lastTop = null;
  let stableFrames = 0;

  const performScroll = (el) => {
    const top = getElementScrollY(el, offset);
    scrollToPosition(top, behavior);

    // Snap back if heavy, lazy-loaded content shifts the layout after we scroll.
    const corrections = [300, 800, 1500, 2500, 3500];
    corrections.forEach((delay) => {
      window.setTimeout(() => {
        const current = document.getElementById(sectionId);
        if (!current) return;
        const targetTop = getElementScrollY(current, offset);
        if (Math.abs(window.scrollY - targetTop) > 2) {
          scrollToPosition(targetTop, behavior);
        }
      }, delay);
    });
  };

  const check = () => {
    const el = document.getElementById(sectionId);
    if (!el) {
      if (Date.now() - startedAt < maxWait) {
        window.setTimeout(check, 100);
      }
      return;
    }

    const top = getElementScrollY(el, offset);

    // Only scroll once the element's position stops changing (layout settled).
    if (lastTop !== null && Math.abs(top - lastTop) <= 2) {
      stableFrames += 1;
      if (stableFrames >= 2) {
        performScroll(el);
        return;
      }
    } else {
      stableFrames = 0;
    }
    lastTop = top;

    if (Date.now() - startedAt < maxWait) {
      window.setTimeout(check, 80);
    } else {
      // Timeout reached — scroll to wherever it is now rather than giving up.
      performScroll(el);
    }
  };

  // Give React a chance to paint the freshly-mounted section before measuring.
  window.setTimeout(check, 40);
}

export default scrollToSection;
